import { useState, useCallback, useEffect } from 'react';

const MAX_TABS = 10;
const STORAGE_KEY = 'sutra-tabs';

export function useTabs() {
  const [openTabs, setOpenTabs] = useState(() => {
    // Load tabs from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.tabs || [];
      }
    } catch (error) {
      console.error('Failed to load tabs from localStorage:', error);
    }
    return [];
  });

  const [activeTabId, setActiveTabId] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.activeTabId || null;
      }
    } catch (error) {
      console.error('Failed to load active tab from localStorage:', error);
    }
    return null;
  });

  // Persist tabs to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        tabs: openTabs,
        activeTabId
      }));
    } catch (error) {
      console.error('Failed to save tabs to localStorage:', error);
    }
  }, [openTabs, activeTabId]);

  const openTab = useCallback((tab) => {
    setOpenTabs(prev => {
      // Check if tab already exists
      const existingTab = prev.find(t => t.id === tab.id);
      if (existingTab) {
        // Tab already open, just switch to it
        setActiveTabId(tab.id);
        return prev;
      }

      // Check max tabs limit
      if (prev.length >= MAX_TABS) {
        console.warn(`Maximum of ${MAX_TABS} tabs allowed`);
        return prev;
      }

      // Add new tab
      return [...prev, { ...tab, id: tab.id || `tab-${Date.now()}` }];
    });

    // Set as active tab
    setActiveTabId(tab.id || `tab-${Date.now()}`);
  }, []);

  const closeTab = useCallback((tabId) => {
    setOpenTabs(prev => {
      const filtered = prev.filter(t => t.id !== tabId);

      // If closing the active tab, switch to another tab
      if (activeTabId === tabId) {
        const tabIndex = prev.findIndex(t => t.id === tabId);
        if (filtered.length > 0) {
          // Switch to the tab to the right, or the leftmost tab
          const newActiveIndex = tabIndex < filtered.length ? tabIndex : filtered.length - 1;
          setActiveTabId(filtered[newActiveIndex].id);
        } else {
          setActiveTabId(null);
        }
      }

      return filtered;
    });
  }, [activeTabId]);

  const switchTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, []);

  const closeAllTabs = useCallback(() => {
    setOpenTabs([]);
    setActiveTabId(null);
  }, []);

  const getActiveTab = useCallback(() => {
    return openTabs.find(t => t.id === activeTabId);
  }, [openTabs, activeTabId]);

  return {
    openTabs,
    activeTabId,
    activeTab: getActiveTab(),
    openTab,
    closeTab,
    switchTab,
    closeAllTabs
  };
}
